import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import startCase from 'lodash/startCase'
import toPairs from 'lodash/toPairs'
import { api as http } from '~/api/services'
import ComposeMessageModal from '../ComposeMessageModal'
import FileCollection from '../FileCollection'
import Loading from '../Loading'
import MeetingRequestCFN from '../MeetingRequestCFN'
import ProfileBodyWithTabs from '../ProfileBodyWithTabs'
import ProfileDisclaimerTab from '../ProfileDisclaimerTab'
import ProfileHeaderWithButtons from '../ProfileHeaderWithButtons'
import ProfileOverviewTab from '../ProfileOverviewTab'
import UpcomingMeetingRequests from '../UpcomingMeetingRequests'

const ABOUT_TAB_KEYS = ['opportunity', 'opportunityDetails']
const ABOUT_TAB_KEYS_SIDE = ['imageUrl']

const DISCLAIMER =
  'The provision of any information regarding any fund, fund manager, investment advisor, entity that holds interests in real estate or other classes of assets, or any other investment vehicle (collectively, the "Entity Information") to you is for informational purposes only and does not constitute any offer to sell or the solicitation of an offer to buy any securities or investments. From time to time, such Entity Information may be posted by individuals or entities that have an affiliation with Apex US Distribution LLC or its affiliates. Regardless of whether such an affiliation exists, Apex US Distribution LLC makes no representation about, and does not independently verify, the Entity Information and shall not be responsible for its accuracy, completeness or your reliance thereon. The Entity Information is self-reported by the associated fund, fund manager, investment advisor, general partner, member, or sponsor to Apex US Distribution LLC. Apex US Distribution LLC is not making any recommendation, express or implied, as to the suitability or otherwise of any investment with, and is not acting as agent, promoter, underwriter or placement agent for, any fund, fund manager, investment advisor, entity that holds interests in real estate or other classes of assets, or any other investment vehicle, or their respective associated general partners, members or sponsors, or any other client represented by a fund manager or investment advisor. You must assess the suitability of any particular asset, fund or strategy and carry out appropriate due diligence on your own behalf in relation to the same and Apex US Distribution LLC provides no advice in that regard. Apex US Distribution LLC makes no representation as to the fund’s, fund manager’s, investment advisor’s, general partner’s, member’s, or sponsor’s compliance with any applicable laws or regulations. Apex US Distribution LLC shall not be liable for any losses that may arise, directly or indirectly, from your introduction to, or investment with, any fund, fund manager, investment advisor, entity that holds interests in real estate or other classes of assets, or any other investment vehicle, or their respective general partners, members or sponsors.'
const DealProfileContainer = ({
  deal,
  hideActions = false,
  showHeader = false,
  showMeetings = true,
  openDataroom = false,
}) => {
  const [isRequestMeetingModalVisible, setIsRequestMeetingModalVisible] =
    useState(false)
  const [composeModalVisible, setComposeModalVisible] = useState(false)
  const [activeTab, setActiveTab] = useState(
    openDataroom ? 'documentation' : 'overview'
  )
  const [meetings, setMeetings] = useState([])
  const [loadingMeetings, setLoadingMeetings] = useState(false)

  const getMeetings = useCallback(() => {
    setLoadingMeetings(true)
    http
      .get(`deals/${deal.dealId}/meetings`)
      .then((response) => {
        setMeetings(response.data.result)
      })
      .finally(() => {
        setLoadingMeetings(false)
      })
  }, [deal])

  useEffect(() => {
    if (showMeetings) {
      getMeetings()
    }
  }, [deal, getMeetings, showMeetings])

  const getDealsData = () =>
    map(toPairs(deal), (f) => ({
      label: startCase(f[0]),
      value: f[1],
      name: f[0],
    }))

  const getTabs = () => {
    const dealsData = getDealsData()
    const tabs = [
      {
        key: 'overview',
        title: 'Overview',
        component: (
          <ProfileOverviewTab
            dealDisclaimer
            onDealDisclaimerClick={() => setActiveTab('disclaimer')}
            postedBy={deal.categoryName}
            sections={dealsData.filter(
              (f) =>
                ABOUT_TAB_KEYS.indexOf(f.name) > -1 &&
                !isNil(f.value) &&
                !isEmpty(f.value)
            )}
            sideSections={dealsData.filter(
              (f) =>
                ABOUT_TAB_KEYS_SIDE.indexOf(f.name) > -1 &&
                !isNil(f.value) &&
                !isEmpty(f.value)
            )}
          />
        ),
      },
      {
        key: 'details',
        title: 'Details',
        component: (
          <ProfileOverviewTab
            sections={deal.profile
              .filter((f) => f.groupName === 'Opportunity Details')[0]
              ?.sections.filter((s) => s.value)}
            sideSections={deal.profile
              .filter((f) => f.groupName === 'Content')[0]
              ?.sections.filter(
                (s) =>
                  s.key !== 'ImageUrl' && !isNil(s.value) && !isEmpty(s.value)
              )}
          />
        ),
      },
      {
        key: 'documentation',
        title: 'Documentation',
        component: (
          <FileCollection
            files={deal.documents}
            title={deal.title}
            entityId={deal.dealId}
            type="deal"
          />
        ),
      },
      showMeetings
        ? {
            key: 'meetings',
            title: 'Meetings',
            component: (
              <UpcomingMeetingRequests
                onRefresh={getMeetings}
                meetings={meetings}
                loadingMeetings={loadingMeetings}
                fromDealProfile
              />
            ),
          }
        : null,
      {
        key: 'disclaimer',
        title: 'Disclaimer',
        component: <ProfileDisclaimerTab disclaimer={DISCLAIMER} />,
      },
    ]

    return filter(tabs, (t) => !isNil(t))
  }

  return (
    <Loading spinning={loadingMeetings}>
      {showHeader && (
        <ProfileHeaderWithButtons
          name={deal.title}
          address={deal.manager}
          status={deal.status === 'Closed' ? deal.status : null}
          hideActions={hideActions}
          canSendMeetingRequest={true}
          sendRequestText="Meet with Deal Partner"
          onSendRequest={() => {
            setIsRequestMeetingModalVisible(true)
          }}
          onSendMessage={() => {
            setComposeModalVisible(true)
          }}
        />
      )}
      <ProfileBodyWithTabs
        onTabChange={(tabKey) => {
          setActiveTab(tabKey)
        }}
        activeTabKey={activeTab}
        tabs={getTabs()}
      />
      {!isNil(deal) && !hideActions && (
        <MeetingRequestCFN
          visible={isRequestMeetingModalVisible}
          onClose={() => {
            setIsRequestMeetingModalVisible(false)
          }}
          cfnCompany={deal}
          dealMeeting
          sentToCFN={deal.categoryId === 6}
        />
      )}
      {!isNil(deal) && !hideActions && (
        <ComposeMessageModal
          visible={composeModalVisible}
          onClose={() => {
            setComposeModalVisible(false)
          }}
          companyID={deal.companyId}
        />
      )}
    </Loading>
  )
}

DealProfileContainer.propTypes = {
  deal: PropTypes.shape({
    dealId: PropTypes.number.isRequired,
    companyId: PropTypes.number.isRequired,
    title: PropTypes.string,
    companyName: PropTypes.string,
    manager: PropTypes.string,
    profile: PropTypes.object,
    imageUrl: PropTypes.string,
    categoryName: PropTypes.string,
    documents: PropTypes.array,
    status: PropTypes.string,
    categoryId: PropTypes.number,
  }),
  hideActions: PropTypes.bool,
  showHeader: PropTypes.bool,
  openDataroom: PropTypes.bool,
  showMeetings: PropTypes.bool,
}

export default DealProfileContainer
